import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AccialCreditNoteFieldDict,
  AccialCreditNoteStatusDetailDict,
  AccialCreditNoteStatusDict,
  AccialCreditNoteStatusEnum,
  AccialCreditNoteStatusToneDict
} from 'src/app/core/enums/accialCreditNote.enum';
import {
  AccialCreditNoteAssociationItem,
  AccialCreditNoteAssociationResponse
} from 'src/app/core/interfaces/accial-credit-note';

interface SummaryCount {
  label: string;
  value: number;
  tone: string;
}

/** Orden en que se muestran los ítems: primero lo que necesita atención. */
const TONE_PRIORITY = { error: 0, warning: 1, success: 2, neutral: 3 };

@Component({
  selector: 'app-cargo-credit-note-association-summary',
  templateUrl: './cargo-credit-note-association-summary.component.html',
  styleUrls: ['./cargo-credit-note-association-summary.component.scss']
})
export class CargoCreditNoteAssociationSummaryComponent implements OnInit {
  public response: AccialCreditNoteAssociationResponse;
  public counts: SummaryCount[] = [];
  public items: AccialCreditNoteAssociationItem[] = [];
  public statusDict = AccialCreditNoteStatusDict;
  public statusDetailDict = AccialCreditNoteStatusDetailDict;
  public statusToneDict = AccialCreditNoteStatusToneDict;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogParams: { response: AccialCreditNoteAssociationResponse },
    public dialogRef: MatDialogRef<CargoCreditNoteAssociationSummaryComponent>,
  ) { }

  ngOnInit() {
    this.response = this.dialogParams && this.dialogParams.response ? this.dialogParams.response : null;
    if (!this.response) return;
    this.counts = this.buildCounts(this.response);
    this.items = this.sortItems(this.response.items);
  }

  /** Deja sólo los contadores con valor, para no llenar el resumen de ceros. */
  private buildCounts(response: AccialCreditNoteAssociationResponse): SummaryCount[] {
    const all: SummaryCount[] = [
      { label: 'Creadas', value: response.createdCount, tone: 'success' },
      { label: 'Actualizadas', value: response.updatedCount, tone: 'success' },
      { label: 'Sin cambios', value: response.unchangedCount, tone: 'neutral' },
      { label: 'Con diferencias', value: response.processedWithDifferencesCount, tone: 'warning' },
      { label: 'No encontradas', value: response.notFoundCount, tone: 'error' },
      { label: 'Sin factura', value: response.billNotFoundCount, tone: 'error' },
      { label: 'Conflicto', value: response.conflictCount, tone: 'error' },
      { label: 'Con error', value: response.errorCount, tone: 'error' },
    ];
    return all.filter((count: SummaryCount) => !!count.value);
  }

  private sortItems(items: AccialCreditNoteAssociationItem[]): AccialCreditNoteAssociationItem[] {
    if (!items || !items.length) return [];
    return items.slice().sort((a, b) => this.tonePriority(a) - this.tonePriority(b));
  }

  private tonePriority(item: AccialCreditNoteAssociationItem): number {
    const tone = this.toneOf(item);
    return TONE_PRIORITY[tone] !== undefined ? TONE_PRIORITY[tone] : TONE_PRIORITY.neutral;
  }

  public toneOf(item: AccialCreditNoteAssociationItem): string {
    if (!item || !item.status) return 'neutral';
    return this.statusToneDict[item.status] ? this.statusToneDict[item.status] : 'neutral';
  }

  public statusLabel(status: AccialCreditNoteStatusEnum): string {
    return this.statusDict[status] ? this.statusDict[status] : status;
  }

  public statusDetail(status: AccialCreditNoteStatusEnum): string {
    return this.statusDetailDict[status] ? this.statusDetailDict[status] : '';
  }

  public fieldLabel(field: string): string {
    return AccialCreditNoteFieldDict[field] ? AccialCreditNoteFieldDict[field] : field;
  }

  /** True cuando ninguna nota crédito terminó en un estado problemático. */
  get isFullySuccessful(): boolean {
    if (!this.response) return false;
    return !this.response.notFoundCount && !this.response.billNotFoundCount &&
      !this.response.conflictCount && !this.response.errorCount &&
      !this.response.processedWithDifferencesCount;
  }
}
